// The Target Value cell's popover body (spec 008 §2.5): a currency-shaped number field, Update, Cancel.
//
// WHETHER a row is editable is decided by opportunityInlineEdit.targetValueEdit, not here — the column
// displays financialNumbers.baselineRevenue for everything but T&M, and that is a computed Financial
// Service number rather than a writable one, so those rows are read-only and never open this editor.
//
// The numeric contract is CreateOpportunityModal's own (^\d*\.?\d*$ on the input, plus an isNaN guard
// for the empty string and the lone "." the regex allows), so negatives are unreachable and the two
// entry points agree. That guard gates the Update button, not just the commit: gating only the commit
// left Update live on a draft that cannot parse, and pressing it returned silently — a dead button.
// D14: display formatting in the cell itself is unchanged — this editor does not touch it.
import React, {useState} from 'react';
import {Button, EMPHASIS, TextField} from '@forecast-it/design-system';
import Styled from 'styled-components';
import Util from '../../../shared/util/util';
import UpdateProjectMutation from '../../../../mutations/update_project_mutation.modern';
import {targetValueCurrent} from '../../opportunityInlineEdit';
import type {OpportunityNode} from '../../opportunityTypes';

export interface TargetValueEditorProps {
	node: OpportunityNode;
	// The writable source for this row, from targetValueEdit().
	onCommit: () => void;
	close: () => void;
	label: string;
	updateLabel: string;
	cancelLabel: string;
}

const Body = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 260px;
	padding: 16px;
`;
const Actions = Styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 8px;
`;

export const TargetValueEditor = ({
	node,
	onCommit,
	close,
	label,
	updateLabel,
	cancelLabel,
}: TargetValueEditorProps): JSX.Element => {
	const stored = targetValueCurrent(node);
	const initial = stored == null ? '' : String(stored);
	const [draft, setDraft] = useState(initial);
	const [saving, setSaving] = useState(false);

	const parsed = parseFloat(draft);
	// The input regex admits "" and a lone ".", neither of which parses. Both have to disable Update as
	// well as stop the commit, or the button is live with nothing to do. Note this makes clearing the
	// field a no-op rather than a way to unset the value — there is no "clear" on this column today.
	const unparsable = isNaN(parsed);

	const save = () => {
		if (draft === initial || unparsable || saving) return;
		setSaving(true);
		// baselineTarget is the only writable source behind this column: it is what T&M rows display, and
		// only T&M rows are editable. Non-T&M rows display a computed baselineRevenue and never reach here.
		Util.CommitMutation(UpdateProjectMutation, {project: {id: node.id}, baselineTarget: parsed}, onCommit);
		close();
	};

	return (
		<Body data-cy="target-value-editor">
			<TextField
				data-cy="inline-target-value"
				label={label}
				value={draft}
				onValueChange={setDraft}
				placeholder="0"
				allowedCharactersRegex="^\d*\.?\d*$"
			/>
			<Actions>
				<Button emphasis={EMPHASIS.LOW} data-cy="inline-target-value-cancel" onPress={close}>
					{cancelLabel}
				</Button>
				<Button
					emphasis={EMPHASIS.HIGH}
					data-cy="inline-target-value-submit"
					disabled={draft === initial || unparsable || saving}
					onPress={save}
				>
					{updateLabel}
				</Button>
			</Actions>
		</Body>
	);
};

export default TargetValueEditor;
